import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StationPicker from '../components/StationPicker';
import DailyCommute from '../components/DailyCommute';
import InfoFluo from '../components/InfoFluo';
import { Train, MapPin, Clock3, Database, ArrowRight, Route } from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Scroll to anchor on hash change (e.g. /#commute, /#info-fluo from header nav)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait one tick so the section is mounted before scrolling
    const id2 = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(id2);
  }, [location.hash, location.key]);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-ter-blue overflow-hidden">
        <div className="absolute inset-0 subtle-grain opacity-30" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-ter-yellow/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-ter-red/20 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-16 text-center">
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
            {t('hero.title_part1')} <span className="text-ter-yellow">{t('hero.title_highlight')}</span> {t('hero.title_part2')}
          </h1>
          <div className="mt-3 text-sm text-white/70 font-medium">{t('hero.subtitle')}</div>
          <p className="mt-5 text-lg text-white/80 max-w-2xl mx-auto">{t('hero.description')}</p>
          <div className="mt-8 max-w-2xl mx-auto text-left">
            <StationPicker />
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { icon: Clock3, label: t('hero.feature_realtime') },
              { icon: MapPin, label: t('hero.feature_platforms') },
              { icon: Route, label: t('hero.feature_route') },
              { icon: Train, label: t('hero.feature_composition') }
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
                <div className="w-9 h-9 rounded-md bg-ter-yellow/20 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-ter-yellow" />
                </div>
                <span className="text-sm font-medium text-white text-left">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mon trajet quotidien */}
      <DailyCommute />

      {/* Info Fluo (TER, Facebook, WhatsApp) + emplacement pub */}
      <InfoFluo />

	  <section className="bg-slate-50 border-t border-slate-200 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-ter-red">
              <Database className="w-4 h-4" /> {t('opendata.eyebrow')}
            </div>
            <h3 className="text-2xl font-extrabold text-ter-blue mt-1">{t('opendata.title')}</h3>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t('opendata.text')}</p>
          </div>
          <Link to="/tableau" className="justify-self-start md:justify-self-end">
            <button className="px-5 py-3 rounded-md bg-ter-blue text-white font-semibold hover:bg-ter-blue-dark transition-colors inline-flex items-center gap-2">
              {t('nav.board')} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
